import { ResourceAdmin } from './components';
import { toast } from 'sonner';
import api from '../lib/api';

export function AdminSuppliers() {
  return (
    <ResourceAdmin
      title="Suppliers" testid="admin-suppliers"
      sub="PRIVATE — supplier information never appears on the public storefront."
      endpoint="suppliers" searchKeys={['supplier_name']}
      fields={[
        { key: 'product_id', label: 'Linked VQ product', type: 'product', productNameKey: 'vq_product_name' },
        { key: 'supplier_name', label: 'Supplier name' },
        { key: 'supplier_contact', label: 'Supplier contact' },
        { key: 'supplier_product_name', label: 'Supplier product name' },
        { key: 'supplier_sku', label: 'Supplier SKU' },
        { key: 'supplier_cost', label: 'Supplier cost (£)', type: 'number' },
        { key: 'supplier_batch', label: 'Supplier batch' },
        { key: 'supplier_coa', label: 'Supplier COA (PDF)', type: 'file' },
        { key: 'lead_time', label: 'Lead time' },
        { key: 'supplier_stock', label: 'Supplier stock level' },
        { key: 'supplier_notes', label: 'Notes', type: 'textarea', full: true },
      ]}
      columns={[
        { key: 'supplier_name', label: 'Supplier', render: (s) => <span className="font-semibold">{s.supplier_name}</span> },
        { key: 'vq_product_name', label: 'VQ Product' },
        { key: 'supplier_product_name', label: 'Their product' },
        { key: 'supplier_cost', label: 'Cost', render: (s) => (s.supplier_cost ? `£${Number(s.supplier_cost).toFixed(2)}` : '—') },
        { key: 'supplier_batch', label: 'Batch' },
        { key: 'lead_time', label: 'Lead time' },
      ]}
    />
  );
}

export function AdminCoas() {
  const afterSave = async (payload) => {
    if (payload.product_id && payload.active !== false) {
      try {
        await api.put(`/admin/products/${payload.product_id}`, {
          coa_available: true,
          coa_document: payload.pdf_url || '',
          batch_number: payload.batch || undefined,
        });
        toast.success('Linked product updated with COA + batch');
      } catch {}
    }
  };
  return (
    <ResourceAdmin
      title="COAs" testid="admin-coas"
      sub="Upload genuine batch-specific certificates. Saving also links the COA + batch to the product. Never upload placeholder results."
      endpoint="coas" searchKeys={['product_name', 'batch']} afterSave={afterSave}
      defaults={{ active: true }}
      fields={[
        { key: 'product_id', label: 'Related product', type: 'product', productNameKey: 'product_name' },
        { key: 'product_name', label: 'Product name (auto from link)' },
        { key: 'batch', label: 'Batch number' },
        { key: 'laboratory', label: 'Testing laboratory' },
        { key: 'test_date', label: 'Test date (YYYY-MM-DD)' },
        { key: 'test_type', label: 'Test type (e.g. HPLC purity, MS identity)' },
        { key: 'result_specification', label: 'Result / specification (as stated on the document)', type: 'textarea', full: true },
        { key: 'pdf_url', label: 'COA PDF', type: 'file' },
        { key: 'active', label: 'Visible in public COA library', type: 'toggle' },
      ]}
      columns={[
        { key: 'product_name', label: 'Product', render: (c) => <span className="font-semibold">{c.product_name}</span> },
        { key: 'batch', label: 'Batch', render: (c) => <span className="font-mono2 text-xs">{c.batch}</span> },
        { key: 'laboratory', label: 'Laboratory' },
        { key: 'test_type', label: 'Test' },
        { key: 'pdf_url', label: 'PDF', render: (c) => (c.pdf_url ? <a href={c.pdf_url} target="_blank" rel="noreferrer" className="underline font-mono2 text-xs">View</a> : '—') },
        { key: 'active', label: 'Live', render: (c) => (c.active ? '✓' : '—') },
      ]}
    />
  );
}

export function AdminFaqs() {
  return (
    <ResourceAdmin
      title="FAQs" testid="admin-faqs"
      sub="Questions shown on the public FAQ page and homepage preview."
      endpoint="faqs" searchKeys={['question']}
      fields={[
        { key: 'question', label: 'Question', full: true },
        { key: 'answer', label: 'Answer', type: 'textarea', rows: 4, full: true },
        { key: 'category', label: 'Category', type: 'select', options: ['Research Use', 'COA & Testing', 'Storage & Handling', 'Payments', 'Shipping', 'Returns', 'Account', 'General'] },
        { key: 'order', label: 'Sort order', type: 'number' },
      ]}
      columns={[
        { key: 'question', label: 'Question', render: (f) => <span className="font-semibold">{f.question}</span> },
        { key: 'category', label: 'Category' },
        { key: 'order', label: 'Order' },
      ]}
    />
  );
}

export function AdminDocs() {
  return (
    <ResourceAdmin
      title="Documentation" testid="admin-docs"
      sub="Documentation Centre pages. Markdown supported (## headings, **bold**, - lists)."
      endpoint="doc_pages" searchKeys={['title']}
      fields={[
        { key: 'title', label: 'Page title' },
        { key: 'slug', label: 'URL slug (auto if blank)' },
        { key: 'category', label: 'Section', type: 'select', options: ['Guides', 'Quality', 'Policies', 'Legal'] },
        { key: 'order', label: 'Sort order', type: 'number' },
        { key: 'content', label: 'Content (markdown)', type: 'textarea', rows: 12, full: true },
        { key: 'legal_review', label: 'Flag: legal review required', type: 'toggle' },
      ]}
      columns={[
        { key: 'title', label: 'Page', render: (d) => <span className="font-semibold">{d.title}</span> },
        { key: 'slug', label: 'Slug', render: (d) => <span className="font-mono2 text-xs">/docs/{d.slug}</span> },
        { key: 'category', label: 'Section' },
        { key: 'legal_review', label: 'Legal', render: (d) => (d.legal_review ? <span className="font-mono2 text-[10px] uppercase text-[#D97706]">Review req.</span> : '—') },
      ]}
    />
  );
}

export function AdminDiscounts() {
  return (
    <ResourceAdmin
      title="Discounts" testid="admin-discounts"
      sub="Discount codes validated at checkout."
      endpoint="discounts" searchKeys={['code']}
      defaults={{ type: 'percent', active: true }}
      fields={[
        { key: 'code', label: 'Code (e.g. LAB10)' },
        { key: 'type', label: 'Type', type: 'select', options: [['percent', 'Percentage'], ['fixed', 'Fixed amount (£)']] },
        { key: 'value', label: 'Value', type: 'number' },
        { key: 'min_order', label: 'Minimum order (£)', type: 'number' },
        { key: 'usage_limit', label: 'Usage limit (0 = unlimited)', type: 'number' },
        { key: 'start', label: 'Start (ISO date, optional)' },
        { key: 'end', label: 'End (ISO date, optional)' },
        { key: 'active', label: 'Enabled', type: 'toggle' },
      ]}
      columns={[
        { key: 'code', label: 'Code', render: (d) => <span className="font-mono2 font-bold">{d.code}</span> },
        { key: 'value', label: 'Value', render: (d) => (d.type === 'percent' ? `${d.value}%` : `£${d.value}`) },
        { key: 'used_count', label: 'Used', render: (d) => `${d.used_count || 0}${d.usage_limit ? ` / ${d.usage_limit}` : ''}` },
        { key: 'min_order', label: 'Min order', render: (d) => (d.min_order ? `£${d.min_order}` : '—') },
        { key: 'active', label: 'Live', render: (d) => (d.active ? '✓' : '—') },
      ]}
    />
  );
}

export function AdminShipping() {
  return (
    <ResourceAdmin
      title="Shipping" testid="admin-shipping"
      sub="Shipping methods offered at checkout."
      endpoint="shipping" searchKeys={['name']}
      defaults={{ active: true, destinations: 'United Kingdom', tracked: true }}
      fields={[
        { key: 'name', label: 'Method name' },
        { key: 'price', label: 'Price (£)', type: 'number' },
        { key: 'free_threshold', label: 'Free over (£, 0 = never)', type: 'number' },
        { key: 'estimated_delivery', label: 'Estimated delivery' },
        { key: 'destinations', label: 'Allowed destinations' },
        { key: 'tracked', label: 'Tracked', type: 'toggle' },
        { key: 'signature', label: 'Signature required', type: 'toggle' },
        { key: 'active', label: 'Enabled', type: 'toggle' },
      ]}
      columns={[
        { key: 'name', label: 'Method', render: (s) => <span className="font-semibold">{s.name}</span> },
        { key: 'price', label: 'Price', render: (s) => `£${Number(s.price).toFixed(2)}` },
        { key: 'free_threshold', label: 'Free over', render: (s) => (s.free_threshold ? `£${s.free_threshold}` : '—') },
        { key: 'estimated_delivery', label: 'Delivery' },
        { key: 'active', label: 'Live', render: (s) => (s.active ? '✓' : '—') },
      ]}
    />
  );
}
